import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Pill, TestTube, Activity, Calendar, FileText, Loader2,
  Stethoscope, ClipboardList, FileBox, ExternalLink, ChevronRight, NotebookPen,
  Eye, EyeOff, FileImage, FileType2, ScrollText
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { formatDateIST } from '../utils/date';
import { formatDoctorName } from '../utils/doctor';
import { PastVisitDrawer } from './PastVisitDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Right-hand drawer that surfaces every detail we have for an external EMR
// appointment — vitals, diagnosis, prescription, investigations, scanned
// reports — in a single click-through. If a local consultation exists, the
// "View full consultation" button hands off to the richer PastVisitDrawer.
export function AppointmentDetailDrawer({ appointmentId, onClose, isCurrentDoctor = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLocalConsultation, setShowLocalConsultation] = useState(false);

  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/api/appointments/${appointmentId}/details`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) { setData(data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || 'Failed to load visit'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [appointmentId]);

  const appt = data?.appointment || {};
  const vitals = data?.vitals || {};
  const vitalsList = [
    ['BP', vitals.bp],
    ['Pulse', vitals.pulse && `${vitals.pulse} bpm`],
    ['Temp', vitals.temperature && `${vitals.temperature}°F`],
    ['SpO₂', vitals.spo2 && `${vitals.spo2}%`],
    ['Resp', vitals.respiration_rate && `${vitals.respiration_rate}/min`],
    ['Weight', vitals.weight && `${vitals.weight} kg`],
    ['Height', vitals.height && `${vitals.height} cm`],
    ['BMI', vitals.bmi],
  ].filter(([, v]) => v && String(v).trim());
  const diagnosis = data?.diagnosis || [];
  const localConsultationId = data?.local_consultation_id;
  const localConsultation = data?.local_consultation || null;
  // Merge external EMR prescription + local consultation prescription items.
  // The doctor may have saved Rx in this app that hasn't been posted to the
  // pharmacy yet; we want them visible here too. De-dupe on medicine name.
  const externalRx = data?.prescription || [];
  const localRxItems = ((localConsultation?.sections?.prescription?.items) || []).map((m) => ({
    medicine: m.medicine_name || m.name || '',
    composition: m.composition || '',
    dosage: m.dosage || '',
    frequency: m.frequency || '',
    intake: m.intake ? String(m.intake).replace(/_/g, ' ') : '',
    duration: m.duration || '',
    quantity: m.quantity || '',
    status: 'In-app',
    order_id: '',
    remarks: m.remarks || '',
    date: null,
    _source: 'local',
  }));
  const seenRxNames = new Set(externalRx.map((r) => (r.medicine || '').toLowerCase()));
  const prescription = [
    ...externalRx,
    ...localRxItems.filter((r) => r.medicine && !seenRxNames.has(r.medicine.toLowerCase())),
  ];
  // Same merge for investigations.
  const externalInv = data?.investigations || [];
  const localInvItems = ((localConsultation?.sections?.investigations?.items) || []).map((t) => ({
    test_name: typeof t === 'string' ? t : (t.name || t.test_name || ''),
    notes: typeof t === 'object' ? (t.notes || '') : '',
    doctor_name: localConsultation?.doctor_name || '',
    date: null,
    status_number: 0,
    order_number: '',
    test_number: '',
    _source: 'local',
  }));
  const seenInvNames = new Set(externalInv.map((i) => (i.test_name || '').toLowerCase()));
  const investigations = [
    ...externalInv,
    ...localInvItems.filter((t) => t.test_name && !seenInvNames.has(t.test_name.toLowerCase())),
  ];
  const reports = data?.reports || [];
  const consultants = data?.consultants || [];
  const dischargeSummary = data?.discharge_summary || null;
  const isIP = (appt.visit_type || '').trim().toUpperCase() === 'IP';
  const hasDischargeSummary = isIP && dischargeSummary &&
    typeof dischargeSummary.content === 'string' &&
    (
      // Inline HTML (legacy + mammoth-converted from .docx) → strip
      // tags and check for actual text content.
      dischargeSummary.content.replace(/<[^>]+>/g, '').trim().length > 0
      // OR we have a download URL (mammoth conversion failed but the
      // .docx itself is still accessible).
      || (typeof dischargeSummary.content_url === 'string' && dischargeSummary.content_url.startsWith('http'))
    );

  // Pick narrative sections from the local consultation (the doctor's free-
  // text notes captured in this app). External EMR's `prescription` and
  // `investigations_advised` collections cover structured Rx/Tests; this
  // pulls the doctor's HPI / examination / treatment plan / advice / etc.
  const localSections = localConsultation?.sections || {};
  const NARRATIVE_KEYS = [
    ['chief_complaints', 'Chief Complaints'],
    ['past_history', 'Past History / Comorbidities'],
    ['tumor_board', 'Tumor Board'],
    ['clinical_examination', 'Clinical Examination'],
    ['provisional_diagnosis', 'Provisional Diagnosis'],
    ['final_diagnosis', 'Final Diagnosis'],
    ['treatment_plan', 'Treatment Plan'],
    ['visit_notes', 'Visit Notes / Advice'],
  ];
  const narrativeEntries = NARRATIVE_KEYS
    .map(([key, label]) => {
      const sec = localSections[key];
      const content = (sec && typeof sec === 'object') ? sec.content : sec;
      return { key, label, content };
    })
    .filter(({ content }) => {
      if (!content || typeof content !== 'string') return false;
      // Strip HTML tags to detect "real" content (TipTap stores empty as
      // <p></p> which we want to treat as empty).
      return content.replace(/<[^>]+>/g, '').trim().length > 0;
    });
  const reviewDate = (localSections.review_date && typeof localSections.review_date === 'object')
    ? localSections.review_date.date : null;
  const admissionRequest = (localSections.admission_request && typeof localSections.admission_request === 'object')
    ? localSections.admission_request.data : null;
  const hasNarrative = narrativeEntries.length > 0 || reviewDate || admissionRequest;

  // If user explicitly chose to open the rich local consultation, hand off
  // to the existing PastVisitDrawer (which has the full TipTap narrative).
  if (showLocalConsultation && localConsultationId) {
    return (
      <PastVisitDrawer
        consultationId={localConsultationId}
        title={`Visit · ${appt.doctor_name ? formatDoctorName(appt.doctor_name) : ''}`}
        onClose={() => { setShowLocalConsultation(false); onClose(); }}
      />
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-50 transition-opacity"
        onClick={onClose}
        data-testid="appt-detail-backdrop"
      />
      <div
        className="fixed right-0 top-0 bottom-0 z-50 w-[60vw] max-w-3xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        data-testid="appt-detail-drawer"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-700 via-blue-800 to-teal-800 text-white p-5 flex-shrink-0">
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-sm font-bold">{formatDateIST(appt.date)}</span>
                {appt.time && <span className="text-sm text-blue-100">{appt.time}</span>}
                {appt.visit_type && (
                  <Badge className="bg-white/20 text-white border-0 text-[10px] uppercase tracking-wide">
                    {appt.visit_type}
                  </Badge>
                )}
                <Badge className="bg-white/15 text-white border-0 text-[10px] font-mono">
                  {appt.appointment_id}
                </Badge>
              </div>
              <p className="text-sm font-medium">
                {formatDoctorName(appt.doctor_name)}
                {isCurrentDoctor && (
                  <span className="ml-1 text-[10px] bg-white/25 rounded px-1.5 py-0.5 align-middle">
                    You
                  </span>
                )}
              </p>
              {appt.specialisation && (
                <p className="text-xs text-blue-100 mt-0.5">{appt.specialisation}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="close-appt-detail-drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {appt.status && (
              <Badge className="bg-white/15 text-white border-0 text-[10px]">
                {String(appt.status).replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </Badge>
            )}
            {appt.payment_done ? (
              <Badge className="bg-emerald-300/20 text-emerald-100 border-0 text-[10px]">Paid</Badge>
            ) : (
              <Badge className="bg-orange-300/20 text-orange-100 border-0 text-[10px]">Unpaid</Badge>
            )}
            {appt.vital_status && (
              <Badge className="bg-white/15 text-white border-0 text-[10px]">
                Vitals {appt.vital_status === 'completed' ? 'Done' : 'Pending'}
              </Badge>
            )}
            {appt.scheme && (
              <Badge className="bg-white/15 text-white border-0 text-[10px]">{appt.scheme}</Badge>
            )}
            {localConsultationId && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowLocalConsultation(true)}
                className="ml-auto text-white hover:bg-white/15 h-7 gap-1 text-[11px]"
                data-testid="view-full-consultation-btn"
              >
                View full consultation <ChevronRight className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Vitals strip — surfaced inline in the header (instead of a
              separate tab) so the doctor sees the headline numbers at a
              glance every time the drawer opens. Empty/missing vitals are
              filtered out by `vitalsList`. */}
          {vitalsList.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/15" data-testid="appt-header-vitals">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Activity className="h-3 w-3 text-blue-100" />
                <span className="text-[10px] uppercase tracking-wider text-blue-100 font-bold">
                  Vitals
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {vitalsList.map(([label, value]) => (
                  <div
                    key={label}
                    className="bg-white/10 rounded-lg px-2.5 py-1 leading-tight"
                    data-testid={`appt-header-vital-${label.toLowerCase()}`}
                  >
                    <p className="text-[9px] text-blue-100 uppercase tracking-wider font-semibold">
                      {label}
                    </p>
                    <p className="text-sm font-bold text-white">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Consultants strip — surfaces every consultant who touched this
              visit (multi-specialist IP rounds, second opinions, etc.). */}
          {consultants.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="appt-consultants-strip">
              <span className="text-[10px] uppercase tracking-wider text-blue-100 font-bold">
                Consultants ({consultants.length})
              </span>
              {consultants.map((c, i) => (
                <Badge
                  key={i}
                  className="bg-white/15 text-white border-0 text-[10px]"
                  title={c.doctor_type || ''}
                  data-testid={`appt-consultant-${i}`}
                >
                  {formatDoctorName(c.doctor_name)}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading visit details…
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center text-red-500 text-sm p-6 text-center">
            {error}
          </div>
        ) : (
          <Tabs defaultValue={hasDischargeSummary ? 'discharge' : (hasNarrative ? 'notes' : 'prescription')} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className={`mx-4 mt-3 grid ${hasDischargeSummary ? 'grid-cols-6' : 'grid-cols-5'} h-10`}>
              {hasDischargeSummary && (
                <TabsTrigger value="discharge" className="text-xs gap-1" data-testid="appt-tab-discharge">
                  <ScrollText className="h-3.5 w-3.5" /> Discharge
                </TabsTrigger>
              )}
              <TabsTrigger value="notes" className="text-xs gap-1" data-testid="appt-tab-notes">
                <NotebookPen className="h-3.5 w-3.5" /> Notes ({narrativeEntries.length})
              </TabsTrigger>
              <TabsTrigger value="prescription" className="text-xs gap-1" data-testid="appt-tab-prescription">
                <Pill className="h-3.5 w-3.5" /> Rx ({prescription.length})
              </TabsTrigger>
              <TabsTrigger value="investigations" className="text-xs gap-1" data-testid="appt-tab-investigations">
                <TestTube className="h-3.5 w-3.5" /> Tests ({investigations.length})
              </TabsTrigger>
              <TabsTrigger value="diagnosis" className="text-xs gap-1" data-testid="appt-tab-diagnosis">
                <Stethoscope className="h-3.5 w-3.5" /> Diagnosis ({diagnosis.length})
              </TabsTrigger>
              <TabsTrigger value="reports" className="text-xs gap-1" data-testid="appt-tab-reports">
                <FileBox className="h-3.5 w-3.5" /> Reports ({reports.length})
              </TabsTrigger>
            </TabsList>

            {/* Clinical Notes — narrative entered by the doctor in the
                consultation. Pulled from the local consultation's TipTap
                sections (chief complaints, HPI, examination, diagnosis,
                treatment plan, visit notes). */}
            <TabsContent value="notes" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-5 space-y-3">
                  {!localConsultation ? (
                    <EmptyState
                      icon={NotebookPen}
                      text="No clinical notes captured in this app for this visit"
                    />
                  ) : !hasNarrative ? (
                    <EmptyState
                      icon={NotebookPen}
                      text="Doctor has not recorded narrative notes for this visit yet"
                    />
                  ) : (
                    <>
                      {narrativeEntries.map(({ key, label, content }) => (
                        <NarrativeBlock key={key} testId={`appt-narrative-${key}`} label={label} html={content} />
                      ))}
                      {reviewDate && (
                        <div className="flex items-center gap-3 p-4 bg-violet-50 border border-violet-200 rounded-xl">
                          <Calendar className="h-5 w-5 text-violet-600" />
                          <div>
                            <p className="text-[10px] text-violet-600 font-bold uppercase tracking-wider">Review Date</p>
                            <p className="text-sm font-bold text-violet-900">{formatDateIST(reviewDate)}</p>
                          </div>
                        </div>
                      )}
                      {admissionRequest && admissionRequest.requested && (
                        <div className="border border-rose-200 rounded-xl bg-rose-50/50 p-4" data-testid="appt-narrative-admission">
                          <p className="text-[10px] text-rose-700 font-bold uppercase tracking-wider mb-1">Admission Request</p>
                          {admissionRequest.reason && <p className="text-sm text-gray-800"><b>Reason:</b> {admissionRequest.reason}</p>}
                          {admissionRequest.urgency && <p className="text-xs text-gray-700 mt-0.5"><b>Urgency:</b> {admissionRequest.urgency}</p>}
                          {admissionRequest.ward_type && <p className="text-xs text-gray-700 mt-0.5"><b>Ward:</b> {admissionRequest.ward_type}</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Prescription */}
            <TabsContent value="prescription" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-5">
                  {prescription.length === 0 ? (
                    <EmptyState icon={Pill} text="No prescription recorded for this visit" />
                  ) : (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-[2fr_1fr_1fr_60px_70px] gap-2 px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-[10px] font-bold text-blue-900 uppercase tracking-wider">
                        <div>Medicine</div>
                        <div>Frequency</div>
                        <div>Intake</div>
                        <div>Days</div>
                        <div>Qty</div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {prescription.map((rx, i) => (
                          <div
                            key={i}
                            className="grid grid-cols-[2fr_1fr_1fr_60px_70px] gap-2 px-4 py-2.5 items-center text-sm"
                            data-testid={`appt-rx-${i}`}
                          >
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{rx.medicine || '—'}</p>
                              {rx.composition && (
                                <p className="text-[11px] text-blue-600 truncate">{rx.composition}</p>
                              )}
                              {rx.remarks && (
                                <p className="text-[10px] text-gray-500 italic mt-0.5">“{rx.remarks}”</p>
                              )}
                            </div>
                            <div className="text-xs font-mono text-gray-700">{rx.frequency || '—'}</div>
                            <div className="text-xs text-gray-700">{rx.intake || '—'}</div>
                            <div className="text-xs text-gray-700">{rx.duration || '—'}</div>
                            <div className="text-xs font-semibold text-gray-800">{rx.quantity || '—'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Investigations — Finance-enriched: accession number, order
                number, dept, package, head code, order/result status, doctor
                referrer. Renders as detailed cards so the consulting doctor
                can quickly scan order context. */}
            <TabsContent value="investigations" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-5">
                  {investigations.length === 0 ? (
                    <EmptyState icon={TestTube} text="No investigations ordered for this visit" />
                  ) : (
                    <div className="space-y-2">
                      {investigations.map((inv, i) => (
                        <InvestigationCard key={i} inv={inv} index={i} />
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Diagnosis */}
            <TabsContent value="diagnosis" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-5 space-y-3">
                  {diagnosis.length === 0 ? (
                    <EmptyState icon={Stethoscope} text="No diagnosis captured for this visit" />
                  ) : (
                    diagnosis.map((d, i) => (
                      <div
                        key={i}
                        className="border border-gray-200 rounded-xl overflow-hidden"
                        data-testid={`appt-diag-${i}`}
                      >
                        <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-100">
                          <h4 className="text-sm font-bold text-amber-900 capitalize">
                            {(d.type || 'diagnosis').replace(/-/g, ' ')}
                          </h4>
                          {d.date && (
                            <span className="text-[10px] text-amber-700">{formatDateIST(d.date)}</span>
                          )}
                        </div>
                        <div className="px-4 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {d.content || '—'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Vitals */}
            {/* Discharge Summary — only present for IP visits with a final
                summary in PatientPortal.dischargeSummaryContents. Rendered
                with the same prose styling as the doctor's narrative for
                clinical readability. */}
            {hasDischargeSummary && (
              <TabsContent value="discharge" className="flex-1 overflow-hidden m-0">
                <ScrollArea className="h-full">
                  <div className="p-5 space-y-3">
                    <DischargeSummaryCard ds={dischargeSummary} />
                  </div>
                </ScrollArea>
              </TabsContent>
            )}

            {/* Reports — patient-level scanned documents. Each row collapses
                by default; clicking the eye icon expands an inline preview
                (PDF iframe / image tag) below the row. Lazy-loaded so the
                Reports tab opens fast even with 20 reports. */}
            <TabsContent value="reports" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-5 space-y-2">
                  <p className="text-[11px] text-gray-400 mb-2">
                    Patient-level scanned documents (most recent {reports.length}). Click the eye icon to preview inline; the link icon opens in a new tab.
                  </p>
                  {reports.length === 0 ? (
                    <EmptyState icon={FileBox} text="No medical records / reports uploaded yet" />
                  ) : (
                    reports.map((r, i) => <ReportRow key={i} report={r} index={i} />)
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </>
  );
}

// One investigation row — collapsed by default. When `report_url` is present
// (i.e. a finalized lab/radiology PDF exists in the Finance bucket), expose a
// "View Result" toggle that expands an inline PDF iframe below the row. Same
// UX pattern as ReportRow but scoped to per-test result PDFs from
// Finance.investigationsorders.reports.Reportpdf_s3_key.
function InvestigationCard({ inv, index }) {
  const [expanded, setExpanded] = useState(false);
  // `has_report` is the authoritative backend contract; we trust it over
  // report_url alone so an empty URL never re-enables the toggle.
  const hasReport = !!inv.has_report && !!inv.report_url;
  return (
    <div
      className="bg-blue-50/40 border border-blue-100 rounded-xl overflow-hidden"
      data-testid={`appt-inv-${index}`}
    >
      <div className="p-3">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <TestTube className="h-4 w-4 text-blue-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900">{inv.test_name}</p>
              <div className="flex items-center gap-1 flex-wrap">
                {inv.accession_no && (
                  <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px] font-mono" data-testid={`appt-inv-accession-${index}`}>
                    AN: {inv.accession_no}
                  </Badge>
                )}
                {inv.order_number && (
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {inv.order_number}
                  </Badge>
                )}
              </div>
            </div>

            {/* Meta line — referrer / dept / date / status pills */}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {inv.doctor_name && (
                <span className="text-[11px] text-gray-500">By {formatDoctorName(inv.doctor_name)}</span>
              )}
              {inv.department && (
                <span className="text-[10px] text-gray-500">· {inv.department}</span>
              )}
              {inv.head_code && (
                <span className="text-[10px] text-gray-400 font-mono">· {inv.head_code}</span>
              )}
              {inv.date && (
                <span className="text-[11px] text-gray-400">{formatDateIST(inv.date)}</span>
              )}
              {inv.result_status ? (
                <Badge
                  className={`text-[9px] border-0 ${
                    inv.result_status.toLowerCase() === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    inv.result_status.toLowerCase() === 'pending' ? 'bg-amber-100 text-amber-700' :
                    inv.result_status.toLowerCase() === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}
                  data-testid={`appt-inv-result-${index}`}
                >
                  Result: {inv.result_status.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </Badge>
              ) : inv.order_status ? (
                <Badge
                  className="text-[9px] border-0 bg-slate-100 text-slate-600"
                  data-testid={`appt-inv-result-${index}`}
                  title="Result not entered yet by lab"
                >
                  Result: Not collected
                </Badge>
              ) : null}
              {inv.order_status && (
                <Badge className="bg-blue-100 text-blue-800 text-[9px] border-0" data-testid={`appt-inv-order-status-${index}`}>
                  Order: {inv.order_status}
                </Badge>
              )}
            </div>

            {/* Package / scheme / patient context (only when populated) */}
            {(inv.package_name || inv.scheme_name || inv.patient_type_number || inv.account_head || inv.modality) && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {inv.package_name && (
                  <span className="text-[10px] text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">
                    Pkg: {inv.package_name}
                  </span>
                )}
                {inv.scheme_name && (
                  <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                    {inv.scheme_name}
                  </span>
                )}
                {inv.patient_type_number && (
                  <span className="text-[10px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
                    {inv.patient_type_number}
                  </span>
                )}
                {inv.account_head && (
                  <span className="text-[10px] text-slate-600">· {inv.account_head}</span>
                )}
                {inv.modality && (
                  <span className="text-[10px] text-slate-600">· {inv.modality}</span>
                )}
              </div>
            )}
            {inv.notes && (
              <p className="text-[11px] text-gray-600 italic mt-1">{inv.notes}</p>
            )}

            {/* Result PDF actions — only render when a real PDF is available */}
            {hasReport && (
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition ${
                    expanded ? 'bg-emerald-100 text-emerald-800' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  }`}
                  data-testid={`appt-inv-result-toggle-${index}`}
                  title={expanded ? 'Hide result PDF' : 'Preview result PDF inline'}
                >
                  {expanded ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {expanded ? 'Hide Result' : 'View Result'}
                </button>
                <a
                  href={inv.report_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition"
                  data-testid={`appt-inv-result-open-${index}`}
                  title="Open result PDF in new tab"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      {expanded && hasReport && (
        <div className="border-t border-emerald-100 bg-gray-50">
          <iframe
            src={inv.report_url}
            title={`${inv.test_name} — Result`}
            className="w-full h-[600px] bg-white"
            data-testid={`appt-inv-result-pdf-${index}`}
          />
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-16 text-gray-400">
      <Icon className="h-10 w-10 mx-auto mb-3 opacity-40" />
      <p className="text-sm font-medium">{text}</p>
    </div>
  );
}

// Renders one narrative section (TipTap HTML) inside a card. We inject the
// HTML directly because it's authored by trusted in-app doctor users and
// already sanitized by TipTap on the way in.
function NarrativeBlock({ label, html, testId }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={testId}>
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
        <h4 className="text-[11px] font-bold text-gray-700 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>
          {label}
        </h4>
      </div>
      <div
        className="px-4 py-3 text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-800 prose-strong:text-gray-900"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// Detects the report kind from the file URL extension. We use the browser's
// native viewers for both formats (no extra deps) — Chrome/Firefox/Safari all
// render PDFs via <iframe>, and images via <img>. Anything else falls back
// to "open in new tab" only.
function detectReportKind(url) {
  if (!url || typeof url !== 'string') return 'other';
  const clean = url.split('?')[0].toLowerCase();
  if (clean.endsWith('.pdf')) return 'pdf';
  if (/\.(jpe?g|png|webp|gif|bmp|heic|heif)$/.test(clean)) return 'image';
  return 'other';
}

function formatBytes(n) {
  if (!n || typeof n !== 'number') return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Single report row — collapsed by default with file-type icon, name, meta.
// Toggling the eye expands an inline PDF iframe / <img> preview below the
// row. Lazy: the iframe / img is only rendered when expanded.
function ReportRow({ report, index }) {
  const [expanded, setExpanded] = useState(false);
  const kind = detectReportKind(report.file_url);
  const Icon = kind === 'image' ? FileImage : kind === 'pdf' ? FileType2 : FileText;
  const sizeLabel = formatBytes(report.size);
  return (
    <div
      className="border border-gray-200 rounded-xl overflow-hidden bg-white"
      data-testid={`appt-report-${index}`}
    >
      <div className="flex items-center gap-3 p-3 hover:bg-blue-50/30 transition">
        <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
          <Icon className="h-4 w-4 text-purple-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {report.document_name || 'Report'}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {(report.type || []).map((t, j) => (
              <Badge key={j} variant="outline" className="text-[9px]">{t}</Badge>
            ))}
            {report.provider && (
              <span className="text-[10px] text-gray-500">by {report.provider}</span>
            )}
            {report.date && (
              <span className="text-[10px] text-gray-400">{formatDateIST(report.date)}</span>
            )}
            {sizeLabel && (
              <span className="text-[10px] text-gray-400">· {sizeLabel}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {kind !== 'other' && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={`p-1.5 rounded-lg transition ${
                expanded ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
              }`}
              data-testid={`appt-report-toggle-${index}`}
              title={expanded ? 'Hide preview' : 'Preview inline'}
            >
              {expanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
          <a
            href={report.file_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition"
            data-testid={`appt-report-open-${index}`}
            title="Open in new tab"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
      {expanded && kind === 'pdf' && (
        <div className="border-t border-gray-100 bg-gray-50">
          <iframe
            src={report.file_url}
            title={report.document_name || 'PDF preview'}
            className="w-full h-[600px] bg-white"
            data-testid={`appt-report-preview-pdf-${index}`}
          />
        </div>
      )}
      {expanded && kind === 'image' && (
        <div className="border-t border-gray-100 bg-gray-50 p-3 flex items-center justify-center">
          <img
            src={report.file_url}
            alt={report.document_name || 'Report image'}
            className="max-h-[600px] max-w-full rounded-lg shadow-sm"
            loading="lazy"
            data-testid={`appt-report-preview-image-${index}`}
          />
        </div>
      )}
    </div>
  );
}



// IP discharge summary card — surfaces the EMR's final discharge note in a
// dedicated section so the doctor can review the full discharge story
// (diagnosis, course of stay, advice) without leaving the visit drawer.
// The HTML originates from a trusted internal EMR module, so we render it
// directly via dangerouslySetInnerHTML — the same pattern we use for local
// TipTap narratives elsewhere in the drawer.
function DischargeSummaryCard({ ds }) {
  const status = (ds.status || '').toLowerCase();
  const statusColor =
    status === 'final' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
    status === 'draft' ? 'bg-amber-100 text-amber-800 border-amber-200' :
    'bg-slate-100 text-slate-700 border-slate-200';
  // The EMR module can return either inline HTML or a .docx URL. When
  // the URL is present, the backend tried to convert it inline via
  // mammoth — if that conversion produced HTML, render it. If the
  // conversion failed (large/complex docx, network blip), the inline
  // content will be empty but `content_url` is still usable as a
  // direct download.
  const inlineHtml = typeof ds.content === 'string'
    ? ds.content.replace(/<[^>]+>/g, '').trim().length > 0 ? ds.content : ''
    : '';
  const downloadUrl = typeof ds.content_url === 'string' && ds.content_url.startsWith('http')
    ? ds.content_url : '';
  return (
    <div
      className="border border-emerald-200 rounded-xl overflow-hidden bg-white shadow-sm"
      data-testid="appt-discharge-summary-card"
    >
      <div className="px-5 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <ScrollText className="h-4 w-4 text-emerald-700" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-emerald-900" style={{ fontFamily: 'Manrope' }}>
              Discharge Summary
            </h4>
            {ds.ip_number && (
              <p className="text-[10px] text-emerald-700 font-mono">{ds.ip_number}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
              data-testid="appt-discharge-download"
            >
              Open / Download .docx ↗
            </a>
          )}
          {ds.status && (
            <Badge className={`text-[10px] border ${statusColor} uppercase tracking-wider`} data-testid="appt-discharge-status">
              {ds.status}
            </Badge>
          )}
          {ds.updated_at && (
            <span className="text-[10px] text-emerald-700">
              Updated {formatDateIST(ds.updated_at)}
            </span>
          )}
        </div>
      </div>
      {inlineHtml ? (
        <div
          className="px-5 py-4 text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none prose-headings:text-emerald-900 prose-strong:text-gray-900 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5"
          data-testid="appt-discharge-summary-content"
          dangerouslySetInnerHTML={{ __html: inlineHtml }}
        />
      ) : downloadUrl ? (
        <div className="px-5 py-6 text-center" data-testid="appt-discharge-summary-empty">
          <p className="text-sm text-slate-600">This discharge summary is a Word document.</p>
          <p className="text-xs text-slate-500 mt-1">
            Use the "Open / Download .docx" link above to view it.
          </p>
        </div>
      ) : (
        <div className="px-5 py-6 text-center text-sm text-slate-400 italic">
          Discharge summary is empty.
        </div>
      )}
    </div>
  );
}
